<template>
  <ZubieDialog
    :action-button-text="preparing || processing ? 'Please wait...' : 'Try Again'"
    cancel-button-text="Cancel"
    content-class="row justify-center"
    :max-width="!$q.screen.gt.sm ? '100%' : undefined"
    :maximized="!$q.screen.gt.sm"
    :title="title"
    :visible="visible"
    @submit="$emit('retry')"
  >
    <div v-if="preparing || processing" class="row justify-center items-center full-width">
      <q-img class="image full-width light-dimmed" :src="imageDataUrl" />
      <q-spinner-grid class="absolute rotate-180" color="primary" size="50%" />
    </div>
    <div v-else-if="!textBlocks.length" class="row justify-center text-center text-grey-9">
      <div>
        <q-icon color="grey-4" name="search_off" size="300px" style="margin-right: -34px" />
        <div>No text was found in the captured image.</div>
        <div>Press "Try Again" to take a new photo.</div>
      </div>
    </div>
    <div v-else>
      <p>Below is a list of text blocks found from the scan. Please select the text block to use.</p>
      <q-list bordered separator>
        <q-item v-for="block in textBlocks" v-ripple clickable @click="onSelect(block.text)">
          <q-item-section>
            <div class="row items-center">
              <div>{{ block.text }}</div>
              <div v-if="block.valid" class="q-ml-sm text-positive"><q-icon name="check_circle" /> Valid</div>
            </div>
          </q-item-section>
        </q-item>
      </q-list>
    </div>
  </ZubieDialog>
</template>

<script setup lang="ts">
import { useQuasar } from 'quasar';
import { computed, ref } from 'vue';
import ZubieDialog from 'components/ZubieDialog.vue';
import { setTimeoutPromise } from 'src/services/setTimeout';

const props = defineProps<{
  imageDataUrl: string;
  preparing: boolean;
  processing: boolean;
  textBlocks: { text: string; valid?: boolean }[];
}>();

const emit = defineEmits(['ok', 'retry']);

const $q = useQuasar();

const noTextFound = computed(() => !props.textBlocks.length);

const title = computed(() => {
  if (props.preparing) {
    return 'Preparing for scan...';
  }
  if (props.processing) {
    return 'Evaluating Photo...';
  }
  return !noTextFound.value ? 'Choose Scanned Text' : 'No Text Found In Scan';
});

const visible = ref(true);
async function onSelect(text: string) {
  emit('ok', text);
  await setTimeoutPromise(250); // wait for ripple
  visible.value = false;
  await setTimeoutPromise(250); // wait for dialog close animation
}
</script>

<style scoped lang="scss">
.image {
  width: 100%;
}
</style>
